import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PublicNav from '../pages/public/homepage/PublicNav';
import OfferLandingTracker from '@/components/public/OfferLandingTracker';
import PublicFooter from '../components/public/PublicFooter';
import PublicMain from '../components/layout/PublicMain';
import CustomHTMLInjector from '../components/public/CustomHTMLInjector';
import useSiteSEO from '@/hooks/useSiteSEO';
import { PageTypeProvider, usePageTypeContext } from '@/contexts/PageTypeContext';

// Scrolls to `#anchor` when navigating to a route with a hash, and to top
// otherwise. Identical behavior to the previous implementation.
function ScrollToHash() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
      return undefined;
    }

    const id = hash.replace(/^#/, '');
    let attempts = 0;
    const maxAttempts = 25;
    const interval = setInterval(() => {
      attempts += 1;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [pathname, hash]);

  return null;
}

// Injects site-wide SEO: verification tags, GA4 / GTM / FB Pixel /
// Plausible / Umami scripts, and the Organization JSON-LD block.
function SiteWideSEO() {
  const { data: seo, loaded } = useSiteSEO();

  const organizationJsonLd = useMemo(() => {
    if (!loaded || !seo?.organization_jsonld_enabled || !seo?.organization_name) return null;
    const same = (seo.organization_same_as || []).filter(Boolean);
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: seo.organization_name,
      url: seo.organization_url || undefined,
      logo: seo.organization_logo || undefined,
      sameAs: same.length ? same : undefined,
    });
  }, [loaded, seo]);

  if (!loaded) return null;

  return (
    <Helmet>
      {seo.google_verification && (
        <meta name="google-site-verification" content={seo.google_verification} />
      )}
      {seo.bing_verification && (
        <meta name="msvalidate.01" content={seo.bing_verification} />
      )}
      {seo.yandex_verification && (
        <meta name="yandex-verification" content={seo.yandex_verification} />
      )}

      {seo.gtm_id && (
        <script>{`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${seo.gtm_id}');
        `}</script>
      )}
      {seo.ga4_measurement_id && (
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${seo.ga4_measurement_id}`}></script>
      )}
      {seo.ga4_measurement_id && (
        <script>{`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${seo.ga4_measurement_id}');
        `}</script>
      )}
      {seo.plausible_domain && (
        <script defer data-domain={seo.plausible_domain} src="https://plausible.io/js/script.js"></script>
      )}
      {seo.umami_website_id && seo.umami_src && (
        <script defer data-website-id={seo.umami_website_id} src={seo.umami_src}></script>
      )}

      {organizationJsonLd && (
        <script type="application/ld+json">{organizationJsonLd}</script>
      )}
    </Helmet>
  );
}

function LayoutChrome({ children, pageType }) {
  const { pageType: ctxPageType, navPresetOverride, footerPresetOverride } = usePageTypeContext();
  const effective = pageType || ctxPageType || 'marketing';
  const { data: seo } = useSiteSEO();
  return (
    <>
      <SiteWideSEO />
      {seo?.custom_head_html && <CustomHTMLInjector html={seo.custom_head_html} location="head" />}
      {seo?.custom_body_end_html && <CustomHTMLInjector html={seo.custom_body_end_html} location="body-end" />}
      <ScrollToHash />
      <OfferLandingTracker />
      <PublicNav pageType={effective} presetOverrideId={navPresetOverride} />
      <PublicMain>{children}</PublicMain>
      <PublicFooter pageType={effective} presetOverrideId={footerPresetOverride} />
    </>
  );
}

const PublicLayout = ({ children, pageType = null }) => (
  <PageTypeProvider>
    <LayoutChrome pageType={pageType}>{children}</LayoutChrome>
  </PageTypeProvider>
);

export default PublicLayout;
